import { useEffect, useState, useCallback } from 'react';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import {
  QuickLoad,
  waitForQuickloadUrl,
  QuickloadResponse,
} from '@trello/quickload';
import { hydrateApolloCacheFromQuickloadResults } from './hydrateApolloCacheFromQuickloadResults';
import { cachedFlagValue } from './cachedFlagValue';
import { sendErrorEvent } from '@trello/error-reporting';

const getQuickloadResults = async (): Promise<QuickloadResponse[]> => {
  return Promise.all(
    QuickLoad.getQuickloadUrls().map(([url]) => waitForQuickloadUrl(url)),
  );
};

export const useQuickload = () => {
  const [isQuickloading, setIsQuickloading] = useState(true);

  const syncQuickloadData = useCallback(async () => {
    const startTime = new Date().getTime();

    try {
      const results = await getQuickloadResults();
      hydrateApolloCacheFromQuickloadResults(results);

      Analytics.sendOperationalEvent({
        action: 'succeeded',
        actionSubject: 'quickload',
        attributes: {
          waitTime: Date.now() - startTime,
        },
        source: getScreenFromUrl(),
      });
    } catch (err) {
      sendErrorEvent(err);
      Analytics.sendOperationalEvent({
        action: 'failed',
        actionSubject: 'quickload',
        attributes: {
          waitTime: Date.now() - startTime,
        },
        source: getScreenFromUrl(),
      });
    } finally {
      setIsQuickloading(false);
    }
  }, [setIsQuickloading]);

  useEffect(() => {
    if (cachedFlagValue('fep.sync-quickload-to-apollo', false)) {
      syncQuickloadData();
    } else {
      setIsQuickloading(false);
    }
  }, [setIsQuickloading, syncQuickloadData]);

  return isQuickloading;
};
